<template>
  <div
    v-editable="blok"
    class="ArticleListing layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div v-if="blok.headline || richtext">
      <component
        :is="blok.headerTag ?? 'h1'"
        v-if="blok.headline"
        class="type-headline-xl mb-8 text-center"
      >
        {{ blok.headline }}
      </component>
      <div
        v-if="richtext !== '<p></p>'"
        class="type-base mb-24 text-center text-text-subtle"
        v-html="richtext"
      />
    </div>
    <div v-if="blok.showKeywords">
      <SideScroll v-if="isMobile" class="mb-24">
        <div class="flex justify-start gap-8">
          <button
            class="btn btn--sm btn--tab"
            :class="{
              'selected': filterKeyword === '',
            }"
            type="button"
            @click="setFilterKeyword('')"
          >
            {{ $t('articles.latestBtn') }}
          </button>
          <button
            v-for="keyword in keywords"
            :key="keyword"
            class="btn btn--tab btn--sm"
            :class="{
              'selected': keyword === filterKeyword,
            }"
            type="button"
            @click="setFilterKeyword(keyword)"
          >
            {{ $t(`articles.keyword.${keyword}`) }}
          </button>
          <div class="w-16 desk:hidden">&nbsp;</div>
        </div>
      </SideScroll>
      <div v-else class="flex justify-center items-center flex-wrap gap-8 mb-32">
        <button
          class="btn btn--tab btn--tab--sm"
          :class="{
            'selected': filterKeyword === '',
          }"
          type="button"
          @click="setFilterKeyword('')"
        >
          {{ $t('articles.latestBtn') }}
        </button>
        <button
          v-for="keyword in keywords"
          :key="keyword"
          class="btn btn--tab btn--sm"
          :class="{
            'selected': keyword === filterKeyword,
          }"
          type="button"
          @click="setFilterKeyword(keyword)"
        >
          {{ $t(`articles.keyword.${keyword}`) }}
        </button>
        <div class="w-16 desk:hidden">&nbsp;</div>
      </div>
    </div>
    <div v-if="blok.showKeywords || blok.filterKeywords?.length" class="mb-16">
      <div class="text-darkest type-headline-sm">
        {{
          availableArticlesNum > 1 ? $t('articles.total.plur', {num: availableArticlesNum}) : availableArticlesNum === 1 ? $t('articles.total.sing') : $t('articles.total.none')
        }}
      </div>
    </div>
    <div v-if="blok.design === 'sideswipe'" class="relative">
      <client-only>
        <swiper-container
          class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
          :space-between="isMobile ? 4 : 12"
          :slides-per-view="uiStore.isMobile ? 2.1 : 3"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
          }"
          :navigation="joinedStories.length > 3"
          :a11y-prev-slide-message="$t('wcag.arialabel.previousSlide')"
          :a11y-next-slide-message="$t('wcag.arialabel.nextSlide')"
          :a11y-pagination-bullet-message="$t('wcag.arialabel.goToSlide')"
        >
          <swiper-slide
            v-for="(story, index) in joinedStories"
            :key="story.uuid"
            class="type-headline-lg"
          >
            <ArticleListingCard
              :story="story"
              :eager="isTop && index < 2"
            />
          </swiper-slide>
        </swiper-container>
      </client-only>
    </div>
    <div v-else class="grid grid-cols-6 gap-x-8 gap-y-32 desk:gap-x-12 desk:gap-y-48">
      <ArticleListingCard
        v-for="(story, index) in joinedStories"
        :key="story.uuid"
        :story="story"
        :eager="isTop && index < 2"
        :class="{
          'col-span-6 desk:col-span-3 type-headline-xl': index % 8 < 2,
          'col-span-3 desk:col-span-2 type-headline-md desk:type-headline-lg': index % 8 >= 2 && index % 8 < 8
        }"
      />
    </div>

    <!-- load more -->
    <LoadProgress
      v-if="!loading && blok.showLoadMore && blok.limit !== '0'"
      button-text="articles.loadmore"
      :current="currentShowingNum"
      :total="availableArticlesNum"
      text="articles.viewed"
      class="mt-48"
      @load-more="loadMoreStories"
    />
  </div>
</template>

<script setup lang="ts">
import { useGlobalContentStore } from '~/store/globalContent';
import type { DatasourceHeadlineTypes, LinkOnly, StoryBlokArticlePage } from '#constants/types/storyblok';
import ArticleListingCard from '~/storyblok/articles/ArticleListingCard.vue';

import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';
import LoadProgress from '~/components/generic/LoadProgress.vue';
import type { ISbRichtext, ISbStoriesParams } from 'storyblok-js-client';
import useLinkReplacer from '~/composables/useLinkReplacer';
import useStoryblokAdmin from '~/composables/useStoryblokAdmin';
const { isInStoryblokAdmin } = useStoryblokAdmin();

const storyblokApi = await useStoryblokApi();
const uiStore = useUiStore();
const globalContent = useGlobalContentStore();
const { isMobile } = storeToRefs(uiStore);
const { replaceLinksInStoryBlokResponse } = useLinkReplacer();

interface Props {
  _uid: string;
  headline: string,
  headerTag?: DatasourceHeadlineTypes,
  text: ISbRichtext,
  limit: string,
  showLoadMore: boolean,
  manualArticles: LinkOnly[],
  design: '' | 'sideswipe',
  showKeywords: boolean,
  imageAspectRatio: string,
  filterKeywords?: string[]
}

const props = defineProps<{
  blok: Props,
  placement?: number,
}>();

const isTop = computed(() => (props.placement === 0));
const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
const { $toNumber } = useNuxtApp();

const richtext = computed(() => renderRichText(props.blok.text));
const folder = globalContent.articlesFolder;
const loading = ref(false);

const filterKeyword = ref('');

const excludedIds = [ globalContent.pageId ]; // excluding self
/* Getting manually selected articles */
const manualIds = props.blok.manualArticles.map((i) => {
  if (i.link.story?.id) {
    excludedIds.push(i.link.story?.id);
  }
  return i.link.story?.uuid;
});

const totalAutoStories = ref(0);
const manualStories = ref([]);
const autoStories = ref([]);
const autoLoadedPage = ref(1);
const perPage = ref($toNumber(props.blok.limit));

// * * * * * watch inside storyblok
if (isInStoryblokAdmin()) {
  watch(() => props.blok.limit, (newLimit) => {
    perPage.value = $toNumber(newLimit);
    loadAutoStories();
  });

  watch(()=> props.blok.filterKeywords, (newVal) => {
    //filterKeyword.value = newVal ?? '';
    loadAutoStories();
  });
}

// * * * * * start loading thing
const storyblokPayloadBase = {
  version: useRuntimeConfig().public.storyblokStoriesVersion,
  starts_with: (!isInStoryblokAdmin() && !globalContent.isSubFolder ? globalContent.getStoryblokBasePath :  '') +  folder,
  resolve_links: 'url',
  content_type: 'ArticlePage',
  excluding_ids: excludedIds.join(','),
  //page: autoLoadedPage.value,
  sort_by: 'sort_by_date:desc',
} as ISbStoriesParams;

const { data } = await useAsyncData(`article-listing-${props.blok._uid}`, async() => {
  // Loading manual stories
  let loadedManual = [];
  if (manualIds.length) {
    const { data } = await storyblokApi.get('cdn/stories/', {
      version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
      by_uuids: manualIds.join(','),
    });
    if (data?.stories) {
      loadedManual = data.stories;
    }
  }
  const remaining = parseInt(props.blok.limit) - loadedManual.length;
  let loadedAuto = [];
  let serverStoryCount = -1;

  if (remaining > 0) {
    perPage.value = remaining;

    const remainingParams = {
      ...storyblokPayloadBase,
      per_page: remaining,
    };

    if (props.blok.filterKeywords?.length) {
      remainingParams.filter_query = {
        summaryCategories: {
          any_in_array: props.blok.filterKeywords.join(','),
        },
      };
    } else if (filterKeyword.value !== '') {
      remainingParams.filter_query = {
        summaryCategories: {
          any_in_array: filterKeyword.value,
        },
      };
    }

    const { data: storyblokData, headers } = await storyblokApi.get('cdn/stories/', remainingParams);
    if (storyblokData) {
      loadedAuto = storyblokData.stories;
      serverStoryCount = parseInt(headers.total ?? data.stories.length, 10);
    }
  }

  return {
    loadedManual,
    loadedAuto,
    serverStoryCount,
  };
});

if (data.value) {
  manualStories.value = data.value.loadedManual;
  autoStories.value = data.value.loadedAuto;
  totalAutoStories.value = data.value.serverStoryCount;
}

const loadMoreStories = () => {
  loadAutoStories(true);
};
/**
 * Reloads auto stories, either to change keyword, or load more
 */
const loadAutoStories = async(append = false) => {
  if (append) {
    autoLoadedPage.value = autoLoadedPage.value + 1;
  } else {
    autoLoadedPage.value = 1;
  }

  const payload = {
    ...storyblokPayloadBase,
    page: autoLoadedPage.value,
    per_page: perPage.value,
  };
  if (filterKeyword.value !== '') {
    payload.filter_query = {
      summaryCategories: {
        any_in_array: filterKeyword.value,
      },
    };
  } else if (props.blok.filterKeywords?.length) {
    payload.filter_query = {
      summaryCategories: {
        any_in_array: props.blok.filterKeywords.join(','),
      },
    };
  }

  const { data, headers } = await storyblokApi.get('cdn/stories/', payload);

  if (append) {
    autoStories.value = autoStories.value.concat(data.stories);
  } else {
    autoStories.value = data.stories;
    totalAutoStories.value = parseInt(headers.total ?? data.stories.length, 10);
  }
};

const joinedStories = computed<StoryBlokArticlePage[]>(()=>{
  return replaceLinksInStoryBlokResponse(manualStories.value.concat(autoStories.value));
});

const availableArticlesNum = computed<number>(() => {
  return totalAutoStories.value + (manualIds.length ?? 0);
});
const currentShowingNum = computed<number>(()=> {
  return joinedStories.value.length;
});

// * * * * * Keywords
const keywordsIn = await globalContent.getArticleKeywords();
//const keywords = await globalContent.getArticleKeywords();
const keywords = computed(()=> {
  if (props.blok.filterKeywords?.length) {
    return keywordsIn.filter((f) => {
      return props.blok.filterKeywords?.includes(f);
    });
  }
  return keywordsIn;
});

const setFilterKeyword = (keyword: string) => {
  filterKeyword.value = keyword;
  loadAutoStories();
};

onActivated(() => {
  setFilterKeyword('');
});

const jsonLd = {
  '@context': 'https://schema.org/',
  '@type': 'ItemList',
  numberOfItems: joinedStories.value.length,
  itemListElement: joinedStories.value.map((story) => {
    const article = story.content;
    const articleObject = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: article.summaryTitle,
    } as any;

    if (article.summaryImage?.filename) {
      articleObject.image = [
        article.summaryImage.filename,
      ];
    }
    return articleObject;
  }),
} as any;

useJsonld(jsonLd);

</script>

<style scoped lang="postcss">

.slideWidth {
  width: calc(100vw - 48px);
}

</style>
